import { Box, Button, Flex, Heading, Text, VStack, HStack, Grid, GridItem, Icon } from "@chakra-ui/react";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckIcon } from "@chakra-ui/icons";
import { FaCheckCircle } from "react-icons/fa";
import { clearProjectRequest, clearPaymentCache, clearDraftProjectId } from "../../../../store/customerProject/reducer";
import { uploadProjectSelector } from "../../../../store/customerProject/selector";
import { userSelector } from "../../../../store/user/selector";
import { calculateOrderTotal } from "../constants/pricing";

export default function OrderCompletedPage() {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const uploadProject = useSelector(uploadProjectSelector as any) as any;
  const user = useSelector(userSelector as any) as any;

  const total = useMemo(() => {
    const stateTotal = (location.state as any)?.total;
    if (typeof stateTotal === 'number' && stateTotal > 0) {
      return stateTotal;
    }
    const calculated = calculateOrderTotal({
      transitionType: uploadProject.transitionType,
      include_expedited_turnaround: (uploadProject as any).include_expedited_turnaround,
      totalPage: uploadProject.totalPage,
      totalWords: uploadProject.totalWords,
      targetLanguage: uploadProject.targetLanguage || [],
      isNotarized: uploadProject.isNotarized,
      isMailHardCopy: uploadProject.isMailHardCopy,
      include_desktop_publishing: uploadProject.include_desktop_publishing,
      include_proofreading: uploadProject.include_proofreading,
    });
    const serverTotal = Number((uploadProject as any)?.serverTotal || 0) || 0;
    return serverTotal > 0 ? serverTotal : calculated;
  }, [location.state, uploadProject]);

  useEffect(() => {
    dispatch(clearPaymentCache());
    dispatch(clearDraftProjectId());
  }, [dispatch]);

  const handleGoToDashboard = () => {
    dispatch(clearProjectRequest());
    navigate("/customer/dashboard");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const deliveryDate = useMemo(() => {
    const date = new Date();
    const daysToAdd = uploadProject.include_expedited_turnaround ? 2 : 5;
    date.setDate(date.getDate() + daysToAdd);
    return date.toISOString().split('T')[0];
  }, [uploadProject.include_expedited_turnaround]);

  return (
    <Box
      minH="100vh"
      bg="#F5F7FA"
      pt={{ base: "80px", md: "100px" }}
      pb={{ base: "40px", md: "60px" }}
    >
      <Flex
        maxW="800px"
        mx="auto"
        px="24px"
        direction="column"
        gap="32px"
        
      >
        <Box
          bg="white"
          borderRadius="24px"
          border="1px solid #E6ECF3"
          p="24px"
        >
          <VStack spacing="24px" align="stretch">
            {/* Header Section */}
            <Flex direction="column" align="center" textAlign="center" gap="24px">
              <Flex
                w="64px"
                h="64px"
                borderRadius="full"
                bg="#D1FAE5"
                align="center"
                justify="center"
              >
                <Icon as={CheckIcon} w="40px" h="40px" color="#059669" />
              </Flex>
              <Heading
                fontSize="24px"
                fontWeight="600"
                color="#0B0E10"
              >
                Thank you, your order is confirmed!
              </Heading>
              <Text fontSize="24px" fontWeight="600" color="#0B0E10">
                Order#{orderId}
              </Text>
            </Flex>

            {/* Two Column Layout */}
            <Grid
              templateColumns={{ base: "1fr", md: "40% 60%" }}
              bg="#F9FAFB"
              borderRadius="16px"
              border="1px solid"
              borderColor="#E6ECF3"
            >
              {/* Left Column - Account Info */}
              <GridItem
                borderRight={{ base: "none", md: "1px solid" }}
                borderColor={{ base: "transparent", md: "#E6ECF3" }}
                p="24px 32px"
              >
                <VStack spacing="24px" align="stretch">
                  <Text
                    fontSize="14px"
                    fontWeight="500"
                    color="#0B0E10"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                  >
                    ACCOUNT INFO
                  </Text>
                  <VStack spacing="12px" align="stretch">
                    <Text fontSize="16px" color="#0B0E10">
                      {user?.first_name || "First Name"}
                    </Text>
                    <Text fontSize="16px" color="#0B0E10">
                      {user?.last_name || "Last Name"}
                    </Text>
                    <Text fontSize="16px" color="#0B0E10">
                      {user?.email || uploadProject.email || "Email Address"}
                    </Text>
                  </VStack>

                  {/* Features List */}
                  <VStack spacing="12px" align="stretch" mt="24px">
                    <HStack spacing="12px">
                      <Icon as={FaCheckCircle} w="20px" h="20px" color="#3B82F6" />
                      <Text fontSize="16px" color="#0B0E10">
                        100% Accuracy
                      </Text>
                    </HStack>
                    <HStack spacing="12px">
                      <Icon as={FaCheckCircle} w="20px" h="20px" color="#3B82F6" />
                      <Text fontSize="16px" color="#0B0E10">
                        Certified Translators
                      </Text>
                    </HStack>
                    <HStack spacing="12px">
                      <Icon as={FaCheckCircle} w="20px" h="20px" color="#3B82F6" />
                      <Text fontSize="16px" color="#0B0E10">
                        10,000+ Happy Customers
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              </GridItem>

              {/* Right Column - Order Details */}
              <GridItem p="24px 32px">
                <VStack spacing="24px" align="stretch">
                  <Text
                    fontSize="14px"
                    fontWeight="500"
                    color="#0B0E10"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                  >
                    ORDER DETAILS
                  </Text>

                  <VStack spacing="8px" align="stretch">
                    <Text fontSize="16px" fontWeight="600" color="#0B0E10">
                      {uploadProject.transitionType === "certified"
                        ? "Certified Translation"
                        : "Standard Translation"}
                    </Text>
                    <Text fontSize="16px" color="#0B0E10">
                      Charged:{" "}
                      {uploadProject.transitionType === "certified"
                        ? "$25.00 per page"
                        : "$0.10 per word"}
                    </Text>
                    <Text fontSize="16px" color="#0B0E10">
                      (250 max words per page)
                    </Text>
                  </VStack>

                  <Box
                    borderTop="1px solid"
                    pt="24px"
                    mt="8px"
                    borderBottom="1px solid"
                    borderColor="#E6ECF3"
                  >
                    <HStack justify="space-between" mb="16px">
                      <Text fontSize="18px" fontWeight="600" color="#0B0E10">
                        Order Total
                      </Text>
                      <Text fontSize="18px" fontWeight="600" color="#0B0E10">
                        {formatCurrency(total)}
                      </Text>
                    </HStack>
                  </Box>

                  <VStack spacing="8px" align="stretch" mt="16px">
                    <Text fontSize="16px" fontWeight="600" color="#0B0E10">
                      Delivery Date
                    </Text>
                    <Text fontSize="16px" color="#0B0E10">
                      Receive your translations by:{" "}
                      <Text as="span" fontWeight="600">
                        {deliveryDate}
                      </Text>
                    </Text>
                  </VStack>
                </VStack>
              </GridItem>
            </Grid>
          </VStack>

                  {/* Button */}
        <Flex justify="center" w="full">
          <Button
            w="full"
            size="lg"
            h="56px"
            borderRadius="full"
            px="48px"
            bg="#3A7BFF"
            color="white"
            mt="24px"
            fontSize="16px"
            fontWeight="600"
            border="1px solid #2C63E5"
            onClick={handleGoToDashboard}
            _hover={{
              bgColor: "#FFFFFF",
              color: "#2C63E5",
            }}
            _active={{
              bgColor: "#FFFFFF",
              color: "#2C63E5",
            }}
          >
            Go to Dashboard
          </Button>
        </Flex>
        </Box>


      </Flex>
    </Box>
  );
}
